/**
 * Dashboard con contatori reali (completamento E10 slice 1).
 *
 * I dati arrivano dagli hook feature esistenti (TanStack Query): prodotti dal
 * product-service, CVE globali dall'aggregation-service, timer SLA dall'srp-service.
 * I conteggi derivati sono pure function in dashboardStats.ts.
 */

import { useAuth, displayName } from '../auth/index.js';
import { useProducts } from '../features/products/hooks.js';
import { useGlobalCves } from '../features/cve/hooks.js';
import { useSlaTimers } from '../features/srp/hooks.js';
import { countKnownExploited, countNewCves, countSlaAtRisk } from './dashboardStats.js';
import styles from './DashboardPage.module.css';

interface StatCard {
  testId: string;
  title: string;
  value: string;
  note: string;
}

/** Valore card da stato query: caricamento → …, errore → —, dati → conteggio. */
function statValue(query: { isPending: boolean; isError: boolean }, count: () => number): string {
  if (query.isPending) return '…';
  if (query.isError) return '—';
  return String(count());
}

export function DashboardPage() {
  const { claims } = useAuth();
  const products = useProducts();
  const cves = useGlobalCves();
  const timers = useSlaTimers();

  const stats: StatCard[] = [
    {
      testId: 'stat-products',
      title: 'Prodotti registrati',
      value: statValue(products, () => (products.data ?? []).length),
      note: 'anagrafica prodotti',
    },
    {
      testId: 'stat-kev',
      title: 'CVE sfruttate note (KEV)',
      value: statValue(cves, () => countKnownExploited(cves.data ?? [])),
      note: 'sul portfolio corrente',
    },
    {
      testId: 'stat-sla',
      title: 'SRP in scadenza',
      value: statValue(timers, () => countSlaAtRisk(timers.data ?? [])),
      note: 'timer SLA in warning o oltre',
    },
    {
      testId: 'stat-new-cves',
      title: 'CVE nuove (7gg)',
      value: statValue(cves, () => countNewCves(cves.data ?? [], new Date())),
      note: 'rilevate negli ultimi 7 giorni',
    },
  ];

  return (
    <div className={styles.page} data-testid="dashboard-page">
      <h1 className={styles.heading}>Benvenuto{claims ? `, ${displayName(claims)}` : ''}</h1>
      <p className={styles.subtitle}>Panoramica della piattaforma CRA Tenacious C</p>

      <div className={styles.grid} data-testid="dashboard-stats">
        {stats.map((stat) => (
          <div key={stat.testId} className={styles.card} data-testid={stat.testId}>
            <p className={styles.cardTitle}>{stat.title}</p>
            <p className={styles.cardValue}>{stat.value}</p>
            <p className={styles.cardNote}>{stat.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
